import { createApi } from '@reduxjs/toolkit/query/react';
import dynamicBaseQueryWithLogger from '@/services/utils/dynamicBaseQueryWithLogger';
import { handlerErrorResponse, handleUnwrapRTKError } from '@/services/utils/handlers';
import { setAppNotice } from '@/app/store/appShellSlice';
import { generateIdempotencyKey } from '@/services/utils/idempotency';

// Se prioriza el microservicio de inventario.
const BASE_URL = process.env.NEXT_PUBLIC_INVENTORY_API_URL ?? 'http://localhost:8082';

export interface ApiResponseBase<T = unknown> {
  success: boolean;
  message: string;
  data: T;
}

export type ItemSort =
  | 'created_at'
  | '-created_at'
  | 'sku'
  | '-sku'
  | 'name'
  | '-name'
  | 'stock'
  | '-stock'
  | 'available'
  | '-available';

export type ItemStockStatus = 'out' | 'low' | 'alert';
export type ReservationPolicy = 'optional' | 'required' | 'forbidden';

export type UomCatalogItem = {
  id: number;
  code: string;
  name: string;
};

export type CreateItemInput = {
  sku: string;
  name: string;
  description: string;
  category_id: number;
  supplier_id?: number | null;
  uom_code?: string | null;
  buy_price: number;
  sell_price?: number | null;
  min_stock_level?: number | null;
  track_serial: boolean;
  track_batch: boolean;
  track_expiry: boolean;
  reservation_policy?: ReservationPolicy | null;
};

export type UpdateItemInput = Omit<CreateItemInput, 'sku'>;

export type CreateItemResult = { id: number; sku: string };
export type UpdateItemResult = { id: number };
export type ToggleItemActiveResult = { id: number; is_active: boolean };

export type ItemDetail = {
  id: number;
  sku: string;
  name: string;
  description: string;
  category_id: number;
  category_name?: string | null;
  supplier_id?: number | null;
  supplier_name?: string | null;
  uom_code: string;
  buy_price: number;
  sell_price?: number | null;
  min_stock_level?: number | null;
  track_serial: boolean;
  track_batch: boolean;
  track_expiry: boolean;
  reservation_policy?: ReservationPolicy | null;
  created_at?: string;
  updated_at?: string;
};

export type StockRecord = {
  inventory_id: number;
  sku?: string | null;
  branch_id: number;
  qty_on_hand: number;
  qty_reserved: number;
  qty_available: number;
  min_stock_level?: number | null;
};

export type LedgerEntry = {
  id: number;
  occurred_at: string; // ISO
  movement_type: string;
  qty_delta: number;
  inventory_id: number;
  unit_cost?: number | null;
  doc_type?: string | null;
  doc_id?: number | null;
  source_type?: string | null;
  source_id?: number | null;
  source_line_id?: number | null;
  branch_id: number;
  available_before?: number | null;
  available_after?: number | null;
  serial_id?: number | null;
  batch_id?: number | null;
  idempotency_key?: string | null;
  created_by?: number | null;
};

export type InventoryListItem = {
  id: number;
  sku: string;
  name: string;
  category_id?: number | null;
  category_name?: string | null;
  supplier_id?: number | null;
  supplier_name?: string | null;
  uom_code?: string | null;
  buy_price?: number | null;
  sell_price?: number | null;
  track_serial?: boolean;
  track_batch?: boolean;
  track_expiry?: boolean;
  reservation_policy?: ReservationPolicy | null;
  qty_on_hand?: number | null;
  qty_reserved?: number | null;
  qty_available?: number | null;
  min_stock_level?: number | null;
};

export type PagedInventoryItems = {
  items: InventoryListItem[];
  total: number;
  page: number;
  page_size: number;
};

export type ListItemsArgs = {
  q?: string;
  category_id?: number | string;
  category_ids?: number[] | string;
  exclude_category_ids?: number[] | string;
  supplier_id?: number | string;
  stock_status?: ItemStockStatus;
  inventory_scope?: 'all' | 'operational';
  page?: number;
  page_size?: number;
  limit?: number;
  offset?: number;
  sort?: ItemSort;
};

/** Se tipa una línea para validar stock por inventario. */
export type StockValidateLine = {
  inventory_id: number;
  qty: number;
};

/** Se tipa el payload para validar stock disponible. */
export type StockValidateInput = {
  branch_id?: number;
  lines: StockValidateLine[];
};

/** Se tipa el resultado por línea de la validación de stock. */
export type StockValidateLineResult = {
  inventory_id: number;
  requested?: number | null;
  qty_on_hand?: number | null;
  qty_reserved?: number | null;
  available?: number | null;
  ok?: boolean | null;
};

/** Se tipa la respuesta de validación de stock. */
export type StockValidateResult = {
  branch_id?: number | null;
  valid?: boolean | null;
  invalid_ids?: number[] | null;
  details?: StockValidateLineResult[] | null;
};

export type CreateSerialInput = {
  inventory_id: number;
  serial_code: string;
  branch_id?: number | null;
};

export type SerialStatus = 'in_stock' | 'allocated' | 'consumed' | 'returned' | 'lost';
export type BatchStatus = 'available' | 'depleted' | 'expired';

export type SerialRecord = {
  id: number;
  inventory_id: number;
  sku: string;
  item_name: string;
  serial_code: string;
  current_branch_id?: number | null;
  purchase_order_id?: number | null;
  purchase_receipt_id?: number | null;
  status: SerialStatus;
  is_available: boolean;
  last_movement_type?: string | null;
  last_doc_type?: string | null;
  last_doc_id?: number | null;
  last_occurred_at?: string | null;
  created_at?: string | null;
};

export type BatchRecord = {
  id: number;
  inventory_id: number;
  sku: string;
  item_name: string;
  batch_code: string;
  expiry_date?: string | null;
  status: BatchStatus;
  qty_on_hand: number;
  qty_reserved: number;
  qty_available: number;
  last_movement_type?: string | null;
  last_doc_type?: string | null;
  last_doc_id?: number | null;
  last_occurred_at?: string | null;
  created_at?: string | null;
};

export type ListSerialsArgs = {
  q?: string;
  inventory_id?: number | null;
  branch_id?: number | null;
  status?: SerialStatus | null;
  limit?: number;
  offset?: number;
};

export type ListBatchesArgs = {
  q?: string;
  inventory_id?: number | null;
  branch_id?: number | null;
  status?: BatchStatus | null;
  expiry_before?: string;
  limit?: number;
  offset?: number;
};

export type LabelSerialsInput = {
  serial_codes: string[];
  paper_width_mm?: number;
  columns?: number;
  include_qr?: boolean;
};

export type LabelSerialsResponse = {
  base64: string;
  mime: string;
};

export type InventorySearchItem = {
  id: number;
  sku: string;
  code: string;
  name: string;
  category_id: number;
  category_name: string;
  track_serial: boolean;
  track_batch: boolean;
  track_expiry: boolean;
  buy_price: number;
  sell_price: number;
  qty_available: number;
  has_stock: boolean;
  availability_scope: 'branch' | 'company';
  branch_id?: number | null;
};

export type StocktakeHistoryEntry = {
  stocktake_id: number;
  occurred_at: string;
  reason_code: string;
  note?: string | null;
  idempotency_key?: string | null;
  created_by?: number | null;
  expected_qty: number;
  counted_qty: number;
  qty_delta: number;
  reserved_qty: number;
  available_before: number;
  available_after: number;
  ledger_id?: number | null;
  document_status?: 'pending' | 'applied' | null;
};

export type StocktakeDocument = {
  id: number;
  branch_id: number;
  reason_code: string;
  note?: string | null;
  idempotency_key?: string | null;
  created_by?: number | null;
  document_status?: 'pending' | 'applied' | null;
  created_at?: string | null;
  items: Array<{
    id: number;
    inventory_id: number;
    expected_qty: number;
    counted_qty: number;
    qty_delta: number;
    reserved_qty: number;
    available_before: number;
    available_after: number;
    ledger_id?: number | null;
  }>;
};

const normalizeStocktakeDocument = (raw: any): StocktakeDocument => {
  const items = Array.isArray(raw?.items) ? raw.items : [];
  return {
    id: Number(raw?.id ?? 0),
    branch_id: Number(raw?.branch_id ?? 0),
    reason_code: String(raw?.reason_code ?? ''),
    note: raw?.note ?? null,
    idempotency_key: raw?.idempotency_key != null ? String(raw.idempotency_key) : null,
    created_by: raw?.created_by != null ? Number(raw.created_by) : null,
    document_status:
      raw?.document_status === 'pending' || raw?.document_status === 'applied'
        ? raw.document_status
        : null,
    created_at: raw?.created_at != null ? String(raw.created_at) : null,
    items: items.map((item: any) => ({
      id: Number(item?.id ?? 0),
      inventory_id: Number(item?.inventory_id ?? 0),
      expected_qty: Number(item?.expected_qty ?? 0),
      counted_qty: Number(item?.counted_qty ?? 0),
      qty_delta: Number(item?.qty_delta ?? 0),
      reserved_qty: Number(item?.reserved_qty ?? 0),
      available_before: Number(item?.available_before ?? 0),
      available_after: Number(item?.available_after ?? 0),
      ledger_id: item?.ledger_id != null ? Number(item.ledger_id) : null,
    })),
  };
};

const normalizeQtyOnHand = (value: any): number | null => {
  if (value?.qty_on_hand != null) return Number(value.qty_on_hand);
  return null;
};

async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  let binary = '';
  const bytes = new Uint8Array(buffer);
  const chunkSize = 0x8000;
  for (let i = 0; i < bytes.length; i += chunkSize) {
    const chunk = bytes.subarray(i, i + chunkSize);
    binary += String.fromCharCode.apply(null, Array.from(chunk));
  }
  return btoa(binary);
}

export const inventoryApi = createApi({
  reducerPath: 'inventoryApi',
  baseQuery: dynamicBaseQueryWithLogger(BASE_URL),
  tagTypes: ['InventoryItem', 'Stock', 'Ledger', 'Serial', 'Batch', 'UOM', 'Stocktake'],
  endpoints: (builder) => ({
    // GET /v1/uoms
    listUoms: builder.query<ApiResponseBase<UomCatalogItem[]>, void>({
      query: () => ({ url: '/v1/uoms', method: 'GET' }),
      transformResponse: (resp: any): ApiResponseBase<UomCatalogItem[]> => {
        const rows = Array.isArray(resp?.data) ? resp.data : [];
        const data = rows.map((row: any) => ({
          id: Number(row?.id ?? 0),
          code: String(row?.code ?? '')
            .trim()
            .toUpperCase(),
          name: String(row?.name ?? '').trim(),
        }));
        return {
          success: !!resp?.success,
          message: String(resp?.message ?? ''),
          data,
        };
      },
      transformErrorResponse: handlerErrorResponse('Inventory.ListUoms'),
      providesTags: ['UOM'],
    }),

    // GET /v1/items
    listItems: builder.query<ApiResponseBase<PagedInventoryItems>, void | ListItemsArgs>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.q) params.set('q', String(args.q));
        if (args?.category_id != null) params.set('category_id', String(args.category_id));
        if (args?.category_ids != null) {
          const categoryIds = Array.isArray(args.category_ids)
            ? args.category_ids.join(',')
            : String(args.category_ids);
          params.set('category_ids', categoryIds);
        }
        if (args?.exclude_category_ids != null) {
          const excludedCategoryIds = Array.isArray(args.exclude_category_ids)
            ? args.exclude_category_ids.join(',')
            : String(args.exclude_category_ids);
          params.set('exclude_category_ids', excludedCategoryIds);
        }
        if (args?.supplier_id != null) params.set('supplier_id', String(args.supplier_id));
        if (args?.stock_status) params.set('stock_status', String(args.stock_status));
        if (args?.inventory_scope) params.set('inventory_scope', String(args.inventory_scope));
        if (args?.page != null) params.set('page', String(args.page));
        if (args?.page_size != null) params.set('page_size', String(args.page_size));
        if (args?.limit != null) params.set('limit', String(args.limit));
        if (args?.offset != null) params.set('offset', String(args.offset));
        if (args?.sort) params.set('sort', args.sort);
        const q = params.toString();
        return { url: `/v1/items${q ? `?${q}` : ''}`, method: 'GET' } as const;
      },
      transformResponse: (resp: any, _meta, args): ApiResponseBase<PagedInventoryItems> => {
        const rawData = resp?.data ?? {};
        const rawItems: any[] = Array.isArray(rawData?.items) ? rawData.items : [];
        const items: InventoryListItem[] = rawItems.map((r: any) => ({
          qty_on_hand: normalizeQtyOnHand(r),
          id: Number(r?.id ?? 0),
          sku: String(r?.sku ?? ''),
          name: String(r?.name ?? ''),
          category_id: r?.category_id != null ? Number(r.category_id) : null,
          category_name: r?.category_name != null ? String(r.category_name) : null,
          supplier_id: r?.supplier_id != null ? Number(r.supplier_id) : null,
          supplier_name: r?.supplier_name != null ? String(r.supplier_name) : null,
          uom_code: r?.uom_code != null ? String(r.uom_code) : null,
          buy_price: r?.buy_price != null ? Number(r.buy_price) : null,
          sell_price: r?.sell_price != null ? Number(r.sell_price) : null,
          track_serial: r?.track_serial != null ? Boolean(r.track_serial) : undefined,
          track_batch: r?.track_batch != null ? Boolean(r.track_batch) : undefined,
          track_expiry: r?.track_expiry != null ? Boolean(r.track_expiry) : undefined,
          reservation_policy:
            r?.reservation_policy != null
              ? (String(r.reservation_policy).toLowerCase() as ReservationPolicy)
              : null,
          qty_reserved: r?.qty_reserved != null ? Number(r.qty_reserved) : null,
          qty_available: r?.qty_available != null ? Number(r.qty_available) : null,
          min_stock_level: r?.min_stock_level != null ? Number(r.min_stock_level) : null,
        }));
        const total = Number(rawData?.total ?? items.length);
        const pageSize = Number(
          rawData?.page_size ??
            (typeof (args as any)?.limit === 'number' ? (args as any).limit : items.length),
        );
        const page = Number(rawData?.page ?? 1);
        return {
          success: !!resp?.success,
          message: String(resp?.message ?? ''),
          data: { items, total, page, page_size: pageSize },
        };
      },
      transformErrorResponse: handlerErrorResponse('Inventory.ListItems'),
      providesTags: ['InventoryItem'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'No se pudo cargar el listado de productos.',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Inventory.ListItems'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'inventory.items.list',
              endpointName: e?.endpointName || 'Inventory.ListItems',
              validationErrors: {},
            }),
          );
        }
      },
    }),
    // GET /v1/items/{id}
    getItem: builder.query<ApiResponseBase<ItemDetail>, number | string>({
      query: (id) => ({
        url: `/v1/items/${id}`,
        method: 'GET',
      }),
      transformResponse: (resp: unknown): ApiResponseBase<ItemDetail> => {
        const raw =
          resp && typeof resp === 'object' && 'data' in resp
            ? ((resp as { data?: Record<string, unknown>; success?: unknown; message?: unknown })
                .data ?? {})
            : {};

        return {
          success: Boolean(
            resp && typeof resp === 'object' && 'success' in resp
              ? (resp as { success?: unknown }).success
              : false,
          ),
          message: String(
            resp && typeof resp === 'object' && 'message' in resp
              ? ((resp as { message?: unknown }).message ?? '')
              : '',
          ),
          data: {
            id: Number(raw.id ?? 0),
            sku: String(raw.sku ?? ''),
            name: String(raw.name ?? ''),
            description: String(raw.description ?? ''),
            category_id: Number(raw.category_id ?? 0),
            category_name: raw.category_name != null ? String(raw.category_name) : null,
            supplier_id: raw.supplier_id != null ? Number(raw.supplier_id) : null,
            supplier_name: raw.supplier_name != null ? String(raw.supplier_name) : null,
            uom_code: String(raw.uom_code ?? ''),
            buy_price: Number(raw.buy_price ?? 0),
            sell_price: raw.sell_price != null ? Number(raw.sell_price) : null,
            min_stock_level: raw.min_stock_level != null ? Number(raw.min_stock_level) : null,
            track_serial: Boolean(raw.track_serial),
            track_batch: Boolean(raw.track_batch),
            track_expiry: Boolean(raw.track_expiry),
            reservation_policy:
              raw.reservation_policy != null
                ? (String(raw.reservation_policy).toLowerCase() as ReservationPolicy)
                : null,
            created_at: raw.created_at != null ? String(raw.created_at) : undefined,
            updated_at: raw.updated_at != null ? String(raw.updated_at) : undefined,
          },
        };
      },
      transformErrorResponse: handlerErrorResponse('Inventory.GetItem'),
      providesTags: (_result, _error, id) => [{ type: 'InventoryItem', id }],
    }),
    // GET /v1/items/search?query=&limit=
    searchItems: builder.query<
      InventorySearchItem[],
      { query: string; limit?: number; branch_id?: number; scope?: 'branch' | 'company' }
    >({
      query: ({ query, limit = 10, branch_id, scope = 'branch' }) => {
        const params = new URLSearchParams();
        params.set('query', query);
        params.set('limit', String(limit));
        params.set('scope', scope);
        if (branch_id != null) params.set('branch_id', String(branch_id));
        return {
          url: `/v1/items/search?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (resp: ApiResponseBase<InventorySearchItem[]>): InventorySearchItem[] => {
        if (!resp?.success || !Array.isArray(resp.data)) {
          return [];
        }
        const rawItems: any[] = resp.data;
        return rawItems.map((item) => ({
          id: Number(item?.id ?? 0),
          code: String(item?.code ?? ''),
          sku: String(item?.sku ?? ''),
          name: String(item?.name ?? ''),
          category_id: Number(item?.category_id ?? 0),
          category_name: String(item?.category_name ?? ''),
          track_serial: Boolean(item?.track_serial),
          track_batch: Boolean(item?.track_batch),
          track_expiry: Boolean(item?.track_expiry),
          buy_price: Number(item?.buy_price ?? 0),
          sell_price: Number(item?.sell_price ?? 0),
          qty_available: Number(item?.qty_available ?? 0),
          has_stock: Boolean(item?.has_stock),
          availability_scope: item?.availability_scope === 'company' ? 'company' : 'branch',
          branch_id: item?.branch_id != null ? Number(item.branch_id) : null,
        }));
      },
      transformErrorResponse: handlerErrorResponse('Inventory.ItemsSearch'),
      providesTags: ['InventoryItem'],
    }),
    // POST /v1/stock/validate
    validateStock: builder.mutation<StockValidateResult, StockValidateInput>({
      query: (body) => ({
        url: '/v1/stock/validate',
        method: 'POST',
        body,
      }),
      transformResponse: (resp: ApiResponseBase<StockValidateResult>): StockValidateResult => {
        const raw = resp?.data ?? { valid: true, invalid_ids: [], details: [] };
        const rawDetails = Array.isArray(raw?.details) ? raw.details : [];
        return {
          branch_id: raw?.branch_id != null ? Number(raw.branch_id) : null,
          valid: raw?.valid != null ? Boolean(raw.valid) : true,
          invalid_ids: Array.isArray(raw?.invalid_ids)
            ? raw.invalid_ids.map((id) => Number(id))
            : [],
          details: rawDetails.map((detail: any) => ({
            inventory_id: Number(detail?.inventory_id ?? 0),
            requested: detail?.requested != null ? Number(detail.requested) : null,
            qty_on_hand: normalizeQtyOnHand(detail),
            qty_reserved: detail?.qty_reserved != null ? Number(detail.qty_reserved) : null,
            available: detail?.available != null ? Number(detail.available) : null,
            ok: detail?.ok != null ? Boolean(detail.ok) : null,
          })),
        };
      },
      transformErrorResponse: handlerErrorResponse('Inventory.ValidateStock'),
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          dispatch(
            setAppNotice({
              message: 'No se pudo validar el stock disponible.',
              status: null,
              details: 'Endpoint: Inventory.ValidateStock',
              action: 'inventory.stock.validate',
              endpointName: 'Inventory.ValidateStock',
            }),
          );
        }
      },
    }),
    // POST /v1/items
    createItem: builder.mutation<ApiResponseBase<CreateItemResult>, CreateItemInput>({
      query: (body) => {
        const payload = {
          ...body,
          supplier_id: body.supplier_id != null ? body.supplier_id : undefined,
          uom_code: body.uom_code ?? undefined,
          sell_price: body.sell_price != null ? body.sell_price : undefined,
          min_stock_level: body.min_stock_level != null ? body.min_stock_level : undefined,
        };
        return { url: '/v1/items', method: 'POST', body: payload } as const;
      },
      transformErrorResponse: handlerErrorResponse('Inventory.CreateItem'),
      invalidatesTags: ['InventoryItem'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al crear el SKU',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Inventory.CreateItem'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'inventory.items.create',
              endpointName: e?.endpointName || 'Inventory.CreateItem',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // PUT /v1/items/{id}
    updateItem: builder.mutation<
      ApiResponseBase<UpdateItemResult>,
      { id: number | string; body: UpdateItemInput }
    >({
      query: ({ id, body }) => {
        const payload = {
          ...body,
          supplier_id: body.supplier_id != null ? body.supplier_id : undefined,
          uom_code: body.uom_code ?? undefined,
          sell_price: body.sell_price != null ? body.sell_price : undefined,
          min_stock_level: body.min_stock_level != null ? body.min_stock_level : undefined,
        };
        return { url: `/v1/items/${id}`, method: 'PUT', body: payload } as const;
      },
      transformErrorResponse: handlerErrorResponse('Inventory.UpdateItem'),
      invalidatesTags: (_result, _error, arg) => [
        'InventoryItem',
        { type: 'InventoryItem', id: arg.id },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar el producto',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Inventory.UpdateItem'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'inventory.items.update',
              endpointName: e?.endpointName || 'Inventory.UpdateItem',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),
    // PUT /v1/items/{id}/active
    toggleItemActive: builder.mutation<
      ApiResponseBase<ToggleItemActiveResult>,
      { id: number | string; is_active: boolean }
    >({
      query: ({ id, is_active }) => ({
        url: `/v1/items/${id}/active`,
        method: 'PUT',
        body: { is_active },
      }),
      transformResponse: (resp: any): ApiResponseBase<ToggleItemActiveResult> => ({
        success: !!resp?.success,
        message: String(resp?.message ?? ''),
        data: {
          id: Number(resp?.data?.id ?? resp?.data?.ID ?? resp?.id ?? 0),
          is_active:
            resp?.data?.is_active != null ? Boolean(resp.data.is_active) : Boolean(resp?.is_active),
        },
      }),
      transformErrorResponse: handlerErrorResponse('Inventory.ToggleItemActive'),
      invalidatesTags: (_result, _error, arg) => [
        'InventoryItem',
        { type: 'InventoryItem', id: arg.id },
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al actualizar el estatus del producto',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Inventory.ToggleItemActive'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'inventory.items.toggle_active',
              endpointName: e?.endpointName || 'Inventory.ToggleItemActive',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // GET /v1/stock
    getStock: builder.query<
      ApiResponseBase<StockRecord>,
      { inventory_id?: number; sku?: string; branch_id?: number }
    >({
      query: ({ inventory_id, sku, branch_id }) => {
        const params = new URLSearchParams();
        if (inventory_id != null) params.set('inventory_id', String(inventory_id));
        if (sku) params.set('sku', sku);
        if (branch_id != null) params.set('branch_id', String(branch_id));
        const q = params.toString();
        return { url: `/v1/stock${q ? `?${q}` : ''}`, method: 'GET' };
      },
      transformResponse: (resp: ApiResponseBase<any>): ApiResponseBase<StockRecord> => {
        const raw = resp?.data ?? {};
        const qtyOnHand = normalizeQtyOnHand(raw);
        return {
          success: !!resp?.success,
          message: String(resp?.message ?? ''),
          data: {
            inventory_id: Number(raw?.inventory_id ?? 0),
            sku: raw?.sku != null ? String(raw.sku) : null,
            branch_id: Number(raw?.branch_id ?? 0),
            qty_on_hand: Number(qtyOnHand ?? 0),
            qty_reserved: Number(raw?.qty_reserved ?? 0),
            qty_available: Number(raw?.qty_available ?? 0),
            min_stock_level: raw?.min_stock_level != null ? Number(raw.min_stock_level) : null,
          },
        };
      },
      transformErrorResponse: handlerErrorResponse('Inventory.GetStock'),
      providesTags: (_r, _e, arg) => [
        { type: 'Stock', id: arg.inventory_id ?? arg.sku ?? 'stock' },
      ],
    }),

    // GET /v1/ledger
    getLedger: builder.query<
      ApiResponseBase<LedgerEntry[]>,
      {
        inventory_id: number;
        from?: string;
        to?: string;
        branch_id?: number;
        movement_type?: string;
        doc_type?: string;
        doc_id?: number;
        source_type?: string;
        source_id?: number;
        source_line_id?: number;
        sort?: 'occurred_at' | 'qty' | '-qty';
        limit?: number;
        offset?: number;
      }
    >({
      query: ({
        inventory_id,
        from,
        to,
        branch_id,
        movement_type,
        doc_type,
        doc_id,
        source_type,
        source_id,
        source_line_id,
        sort,
        limit,
        offset,
      }) => {
        const params = new URLSearchParams();
        params.set('inventory_id', String(inventory_id));
        if (from) params.set('from', from);
        if (to) params.set('to', to);
        if (branch_id != null) params.set('branch_id', String(branch_id));
        if (movement_type) params.set('movement_type', movement_type);
        if (doc_type) params.set('doc_type', doc_type);
        if (doc_id != null) params.set('doc_id', String(doc_id));
        if (source_type) params.set('source_type', source_type);
        if (source_id != null) params.set('source_id', String(source_id));
        if (source_line_id != null) params.set('source_line_id', String(source_line_id));
        if (sort) params.set('sort', sort);
        if (limit != null) params.set('limit', String(limit));
        if (offset != null) params.set('offset', String(offset));
        const q = params.toString();
        return { url: `/v1/ledger${q ? `?${q}` : ''}`, method: 'GET' } as const;
      },
      transformResponse: (resp: ApiResponseBase<any[]>): ApiResponseBase<LedgerEntry[]> => {
        const rows = Array.isArray(resp?.data) ? resp.data : [];
        return {
          success: !!resp?.success,
          message: String(resp?.message ?? ''),
          data: rows.map((row: any) => ({
            id: Number(row?.id ?? 0),
            inventory_id: Number(row?.inventory_id ?? 0),
            branch_id: Number(row?.branch_id ?? 0),
            occurred_at: String(row?.occurred_at ?? ''),
            movement_type: String(row?.movement_type ?? ''),
            qty_delta: Number(row?.qty_delta ?? 0),
            available_before: row?.available_before != null ? Number(row.available_before) : null,
            available_after: row?.available_after != null ? Number(row.available_after) : null,
            unit_cost: row?.unit_cost != null ? Number(row.unit_cost) : null,
            doc_type: row?.doc_type != null ? String(row.doc_type) : null,
            doc_id: row?.doc_id != null ? Number(row.doc_id) : null,
            source_type: row?.source_type != null ? String(row.source_type) : null,
            source_id: row?.source_id != null ? Number(row.source_id) : null,
            source_line_id: row?.source_line_id != null ? Number(row.source_line_id) : null,
            serial_id: row?.serial_id != null ? Number(row.serial_id) : null,
            batch_id: row?.batch_id != null ? Number(row.batch_id) : null,
            idempotency_key: row?.idempotency_key != null ? String(row.idempotency_key) : null,
            created_by: row?.created_by != null ? Number(row.created_by) : null,
          })),
        };
      },
      transformErrorResponse: handlerErrorResponse('Inventory.GetLedger'),
      providesTags: (_r, _e, arg) => [{ type: 'Ledger', id: arg.inventory_id }],
    }),

    // POST /v1/serials
    getSerials: builder.query<ApiResponseBase<SerialRecord[]>, void | ListSerialsArgs>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.q) params.set('q', String(args.q));
        if (args?.inventory_id != null) params.set('inventory_id', String(args.inventory_id));
        if (args?.branch_id != null) params.set('branch_id', String(args.branch_id));
        if (args?.status) params.set('status', String(args.status));
        if (args?.limit != null) params.set('limit', String(args.limit));
        if (args?.offset != null) params.set('offset', String(args.offset));
        const q = params.toString();
        return { url: `/v1/serials${q ? `?${q}` : ''}`, method: 'GET' } as const;
      },
      transformResponse: (resp: ApiResponseBase<any[]>): ApiResponseBase<SerialRecord[]> => {
        const rows = Array.isArray(resp?.data) ? resp.data : [];
        return {
          success: !!resp?.success,
          message: String(resp?.message ?? ''),
          data: rows.map((row: any) => ({
            id: Number(row?.id ?? 0),
            inventory_id: Number(row?.inventory_id ?? 0),
            sku: String(row?.sku ?? ''),
            item_name: String(row?.item_name ?? ''),
            serial_code: String(row?.serial_code ?? ''),
            current_branch_id:
              row?.current_branch_id != null ? Number(row.current_branch_id) : null,
            purchase_order_id:
              row?.purchase_order_id != null ? Number(row.purchase_order_id) : null,
            purchase_receipt_id:
              row?.purchase_receipt_id != null ? Number(row.purchase_receipt_id) : null,
            status: String(row?.status ?? 'in_stock') as SerialStatus,
            is_available: Boolean(row?.is_available),
            last_movement_type:
              row?.last_movement_type != null ? String(row.last_movement_type) : null,
            last_doc_type: row?.last_doc_type != null ? String(row.last_doc_type) : null,
            last_doc_id: row?.last_doc_id != null ? Number(row.last_doc_id) : null,
            last_occurred_at: row?.last_occurred_at != null ? String(row.last_occurred_at) : null,
            created_at: row?.created_at != null ? String(row.created_at) : null,
          })),
        };
      },
      transformErrorResponse: handlerErrorResponse('Inventory.Serials.List'),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((row) => ({ type: 'Serial' as const, id: row.id })),
              { type: 'Serial' as const, id: 'LIST' },
            ]
          : [{ type: 'Serial' as const, id: 'LIST' }],
    }),

    getSerialById: builder.query<ApiResponseBase<SerialRecord>, number | string>({
      query: (id) => ({ url: `/v1/serials/${id}`, method: 'GET' }),
      transformResponse: (resp: ApiResponseBase<any>): ApiResponseBase<SerialRecord> => ({
        success: !!resp?.success,
        message: String(resp?.message ?? ''),
        data: {
          id: Number(resp?.data?.id ?? 0),
          inventory_id: Number(resp?.data?.inventory_id ?? 0),
          sku: String(resp?.data?.sku ?? ''),
          item_name: String(resp?.data?.item_name ?? ''),
          serial_code: String(resp?.data?.serial_code ?? ''),
          current_branch_id:
            resp?.data?.current_branch_id != null ? Number(resp.data.current_branch_id) : null,
          purchase_order_id:
            resp?.data?.purchase_order_id != null ? Number(resp.data.purchase_order_id) : null,
          purchase_receipt_id:
            resp?.data?.purchase_receipt_id != null ? Number(resp.data.purchase_receipt_id) : null,
          status: String(resp?.data?.status ?? 'in_stock') as SerialStatus,
          is_available: Boolean(resp?.data?.is_available),
          last_movement_type:
            resp?.data?.last_movement_type != null ? String(resp.data.last_movement_type) : null,
          last_doc_type: resp?.data?.last_doc_type != null ? String(resp.data.last_doc_type) : null,
          last_doc_id: resp?.data?.last_doc_id != null ? Number(resp.data.last_doc_id) : null,
          last_occurred_at:
            resp?.data?.last_occurred_at != null ? String(resp.data.last_occurred_at) : null,
          created_at: resp?.data?.created_at != null ? String(resp.data.created_at) : null,
        },
      }),
      transformErrorResponse: handlerErrorResponse('Inventory.Serials.Get'),
      providesTags: (_r, _e, id) => [{ type: 'Serial', id }],
    }),

    createSerial: builder.mutation<ApiResponseBase<unknown>, CreateSerialInput>({
      query: (body) => ({ url: '/v1/serials', method: 'POST', body }),
      transformErrorResponse: handlerErrorResponse('Inventory.CreateSerial'),
      invalidatesTags: ['Serial'],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al crear el serial',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Inventory.CreateSerial'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'inventory.serials.create',
              endpointName: e?.endpointName || 'Inventory.CreateSerial',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    getBatches: builder.query<ApiResponseBase<BatchRecord[]>, void | ListBatchesArgs>({
      query: (args) => {
        const params = new URLSearchParams();
        if (args?.q) params.set('q', String(args.q));
        if (args?.inventory_id != null) params.set('inventory_id', String(args.inventory_id));
        if (args?.branch_id != null) params.set('branch_id', String(args.branch_id));
        if (args?.status) params.set('status', String(args.status));
        if (args?.expiry_before) params.set('expiry_before', args.expiry_before);
        if (args?.limit != null) params.set('limit', String(args.limit));
        if (args?.offset != null) params.set('offset', String(args.offset));
        const q = params.toString();
        return { url: `/v1/batches${q ? `?${q}` : ''}`, method: 'GET' } as const;
      },
      transformResponse: (resp: ApiResponseBase<any[]>): ApiResponseBase<BatchRecord[]> => {
        const rows = Array.isArray(resp?.data) ? resp.data : [];
        return {
          success: !!resp?.success,
          message: String(resp?.message ?? ''),
          data: rows.map((row: any) => ({
            id: Number(row?.id ?? 0),
            inventory_id: Number(row?.inventory_id ?? 0),
            sku: String(row?.sku ?? ''),
            item_name: String(row?.item_name ?? ''),
            batch_code: String(row?.batch_code ?? ''),
            expiry_date: row?.expiry_date != null ? String(row.expiry_date) : null,
            status: String(row?.status ?? 'available') as BatchStatus,
            qty_on_hand: Number(row?.qty_on_hand ?? 0),
            qty_reserved: Number(row?.qty_reserved ?? 0),
            qty_available: Number(row?.qty_available ?? 0),
            last_movement_type:
              row?.last_movement_type != null ? String(row.last_movement_type) : null,
            last_doc_type: row?.last_doc_type != null ? String(row.last_doc_type) : null,
            last_doc_id: row?.last_doc_id != null ? Number(row.last_doc_id) : null,
            last_occurred_at: row?.last_occurred_at != null ? String(row.last_occurred_at) : null,
            created_at: row?.created_at != null ? String(row.created_at) : null,
          })),
        };
      },
      transformErrorResponse: handlerErrorResponse('Inventory.Batches.List'),
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map((row) => ({ type: 'Batch' as const, id: row.id })),
              { type: 'Batch' as const, id: 'LIST' },
            ]
          : [{ type: 'Batch' as const, id: 'LIST' }],
    }),

    getBatchById: builder.query<
      ApiResponseBase<BatchRecord>,
      { id: number | string; branch_id?: number | null }
    >({
      query: ({ id, branch_id }) => {
        const params = new URLSearchParams();
        if (branch_id != null) params.set('branch_id', String(branch_id));
        const q = params.toString();
        return { url: `/v1/batches/${id}${q ? `?${q}` : ''}`, method: 'GET' } as const;
      },
      transformResponse: (resp: ApiResponseBase<any>): ApiResponseBase<BatchRecord> => ({
        success: !!resp?.success,
        message: String(resp?.message ?? ''),
        data: {
          id: Number(resp?.data?.id ?? 0),
          inventory_id: Number(resp?.data?.inventory_id ?? 0),
          sku: String(resp?.data?.sku ?? ''),
          item_name: String(resp?.data?.item_name ?? ''),
          batch_code: String(resp?.data?.batch_code ?? ''),
          expiry_date: resp?.data?.expiry_date != null ? String(resp.data.expiry_date) : null,
          status: String(resp?.data?.status ?? 'available') as BatchStatus,
          qty_on_hand: Number(resp?.data?.qty_on_hand ?? 0),
          qty_reserved: Number(resp?.data?.qty_reserved ?? 0),
          qty_available: Number(resp?.data?.qty_available ?? 0),
          last_movement_type:
            resp?.data?.last_movement_type != null ? String(resp.data.last_movement_type) : null,
          last_doc_type: resp?.data?.last_doc_type != null ? String(resp.data.last_doc_type) : null,
          last_doc_id: resp?.data?.last_doc_id != null ? Number(resp.data.last_doc_id) : null,
          last_occurred_at:
            resp?.data?.last_occurred_at != null ? String(resp.data.last_occurred_at) : null,
          created_at: resp?.data?.created_at != null ? String(resp.data.created_at) : null,
        },
      }),
      transformErrorResponse: handlerErrorResponse('Inventory.Batches.Get'),
      providesTags: (_r, _e, arg) => [{ type: 'Batch', id: arg.id }],
    }),

    // POST /v1/serials/labels → Blob PDF (no se documenta en OpenAPI, se mantiene para compatibilidad)
    labelSerials: builder.mutation<LabelSerialsResponse, LabelSerialsInput>({
      queryFn: async (body, _queryApi, _extraOptions, baseQuery) => {
        const resp = await baseQuery({
          url: '/v1/serials/labels',
          method: 'POST',
          body,
          responseHandler: (response: Response) => response.blob(),
        });
        if (resp.error) {
          return { error: resp.error } as any;
        }
        const blob = resp.data as Blob;
        const base64 = await blobToBase64(blob);
        return { data: { base64, mime: blob.type || 'application/pdf' } };
      },
    }),

    // POST /v1/adjustments (delta only)
    adjustLedger: builder.mutation<
      ApiResponseBase<any>,
      {
        inventory_id: number;
        branch_id: number;
        direction: 'in' | 'out';
        qty: number;
        reason_code: 'damage' | 'expiry' | 'theft' | 'variance' | 'custom';
        note?: string;
        unit_cost?: number;
        idempotencyKey?: string;
      }
    >({
      query: ({ idempotencyKey, ...body }) => {
        const headers: Record<string, string> = {};
        headers['X-Idempotency-Key'] = idempotencyKey || generateIdempotencyKey('inv-adjust');
        return { url: '/v1/adjustments', method: 'POST', body, headers };
      },
      transformErrorResponse: handlerErrorResponse('Inventory.Adjustments'),
      invalidatesTags: (_r, _e, arg) => [
        { type: 'Stock', id: arg.inventory_id },
        'Ledger',
        'InventoryItem',
      ],
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al ajustar stock',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Inventory.Adjustments'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'inventory.ledger.adjust',
              endpointName: e?.endpointName || 'Inventory.Adjustments',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // GET /v1/adjustments?inventory_id=&from=&to=&limit=&offset=
    getAdjustments: builder.query<
      ApiResponseBase<
        Array<{
          occurred_at: string;
          reason_code: string;
          note?: string | null;
          qty_delta?: number | null;
          direction?: 'in' | 'out' | null;
          available_before?: number | null;
          available_after?: number | null;
          unit_cost?: number | null;
          ledger_id?: number | null;
        }>
      >,
      {
        inventory_id: number;
        branch_id?: number;
        from?: string;
        to?: string;
        limit?: number;
        offset?: number;
      }
    >({
      query: ({ inventory_id, branch_id, from, to, limit, offset }) => {
        const params = new URLSearchParams();
        params.set('inventory_id', String(inventory_id));
        if (branch_id != null) params.set('branch_id', String(branch_id));
        if (from) params.set('from', from);
        if (to) params.set('to', to);
        if (limit != null) params.set('limit', String(limit));
        if (offset != null) params.set('offset', String(offset));
        const q = params.toString();
        return { url: `/v1/adjustments${q ? `?${q}` : ''}`, method: 'GET' };
      },
      transformResponse: (resp: any): ApiResponseBase<any[]> => {
        const arr = Array.isArray(resp?.data?.items)
          ? resp.data.items
          : Array.isArray(resp?.data)
            ? resp.data
            : [];
        const normalized = arr.map((e: any) => ({
          occurred_at: String(e?.occurred_at ?? e?.date ?? ''),
          reason_code: String(e?.reason_code ?? e?.reason ?? ''),
          note: e?.note ?? null,
          qty_delta: e?.qty_delta != null ? Number(e.qty_delta) : null,
          direction: e?.direction ?? null,
          available_before: e?.available_before != null ? Number(e.available_before) : null,
          available_after: e?.available_after != null ? Number(e.available_after) : null,
          unit_cost: e?.unit_cost != null ? Number(e.unit_cost) : null,
          ledger_id: e?.ledger_id != null ? Number(e.ledger_id) : null,
        }));
        return { success: !!resp?.success, message: String(resp?.message ?? ''), data: normalized };
      },
      transformErrorResponse: handlerErrorResponse('Inventory.GetAdjustments'),
      providesTags: (_r, _e, arg) => [{ type: 'Ledger', id: arg.inventory_id }],
    }),

    // POST /v1/stocktakes
    createStocktake: builder.mutation<
      ApiResponseBase<StocktakeDocument>,
      {
        branch_id: number;
        reason_code: string;
        note?: string;
        lines: Array<{
          inventory_id: number;
          counted_qty: number;
        }>;
        idempotencyKey?: string;
      }
    >({
      query: ({ idempotencyKey, ...body }) => {
        const headers: Record<string, string> = {};
        headers['X-Idempotency-Key'] = idempotencyKey || generateIdempotencyKey('inv-stocktake');
        return { url: '/v1/stocktakes', method: 'POST', body, headers };
      },
      transformResponse: (resp: any): ApiResponseBase<StocktakeDocument> => {
        return {
          success: !!resp?.success,
          message: String(resp?.message ?? ''),
          data: normalizeStocktakeDocument(resp?.data ?? {}),
        };
      },
      transformErrorResponse: handlerErrorResponse('Inventory.Stocktakes.Create'),
      invalidatesTags: (_r, _e, arg) => {
        const inventoryTags = arg.lines.map((line) => ({
          type: 'Stock' as const,
          id: line.inventory_id,
        }));
        const stocktakeTags = arg.lines.map((line) => ({
          type: 'Stocktake' as const,
          id: line.inventory_id,
        }));
        return [...inventoryTags, ...stocktakeTags, 'Ledger', 'InventoryItem'];
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al registrar conteo físico',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Inventory.Stocktakes.Create'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'inventory.stocktake.create',
              endpointName: e?.endpointName || 'Inventory.Stocktakes.Create',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // POST /v1/stocktakes/{id}/apply
    applyStocktake: builder.mutation<
      ApiResponseBase<StocktakeDocument>,
      {
        stocktake_id: number;
        idempotencyKey?: string;
      }
    >({
      query: ({ stocktake_id, idempotencyKey }) => {
        const headers: Record<string, string> = {};
        headers['X-Idempotency-Key'] =
          idempotencyKey || generateIdempotencyKey('inv-stocktake-apply');
        return { url: `/v1/stocktakes/${stocktake_id}/apply`, method: 'POST', headers };
      },
      transformResponse: (resp: any): ApiResponseBase<StocktakeDocument> => ({
        success: !!resp?.success,
        message: String(resp?.message ?? ''),
        data: normalizeStocktakeDocument(resp?.data ?? {}),
      }),
      transformErrorResponse: handlerErrorResponse('Inventory.Stocktakes.Apply'),
      invalidatesTags: (result) => {
        const itemIds = Array.isArray(result?.data?.items)
          ? result.data.items.map((item) => item.inventory_id)
          : [];
        return [
          ...itemIds.map((inventoryId) => ({ type: 'Stock' as const, id: inventoryId })),
          ...itemIds.map((inventoryId) => ({ type: 'Stocktake' as const, id: inventoryId })),
          'Ledger',
          'InventoryItem',
        ];
      },
      async onQueryStarted(_arg, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled;
        } catch (err) {
          const e = handleUnwrapRTKError(err);
          dispatch(
            setAppNotice({
              message: e?.message || 'Error al conciliar stocktake',
              status: e?.code ?? null,
              details: `Endpoint: ${e?.endpointName ?? 'Inventory.Stocktakes.Apply'}${e?.code ? ` — Código: ${e.code}` : ''}`,
              messageFormatted: '',
              action: 'inventory.stocktake.apply',
              endpointName: e?.endpointName || 'Inventory.Stocktakes.Apply',
              validationErrors: (e as any)?.details ?? (e as any)?.originalError?.errors ?? {},
            }),
          );
        }
      },
    }),

    // GET /v1/stocktakes?inventory_id=&branch_id=&from=&to=&limit=&offset=
    getStocktakes: builder.query<
      ApiResponseBase<StocktakeHistoryEntry[]>,
      {
        inventory_id: number;
        branch_id: number;
        from?: string;
        to?: string;
        limit?: number;
        offset?: number;
      }
    >({
      query: ({ inventory_id, branch_id, from, to, limit, offset }) => {
        const params = new URLSearchParams();
        params.set('inventory_id', String(inventory_id));
        params.set('branch_id', String(branch_id));
        if (from) params.set('from', from);
        if (to) params.set('to', to);
        if (limit != null) params.set('limit', String(limit));
        if (offset != null) params.set('offset', String(offset));
        const q = params.toString();
        return { url: `/v1/stocktakes${q ? `?${q}` : ''}`, method: 'GET' };
      },
      transformResponse: (resp: any): ApiResponseBase<StocktakeHistoryEntry[]> => {
        const arr = Array.isArray(resp?.data?.items)
          ? resp.data.items
          : Array.isArray(resp?.data)
            ? resp.data
            : [];
        const normalized = arr.map((e: any) => ({
          stocktake_id: Number(e?.stocktake_id ?? 0),
          occurred_at: String(e?.occurred_at ?? ''),
          reason_code: String(e?.reason_code ?? ''),
          note: e?.note ?? null,
          idempotency_key: e?.idempotency_key != null ? String(e.idempotency_key) : null,
          created_by: e?.created_by != null ? Number(e.created_by) : null,
          expected_qty: Number(e?.expected_qty ?? 0),
          counted_qty: Number(e?.counted_qty ?? 0),
          qty_delta: Number(e?.qty_delta ?? 0),
          reserved_qty: Number(e?.reserved_qty ?? 0),
          available_before: Number(e?.available_before ?? 0),
          available_after: Number(e?.available_after ?? 0),
          ledger_id: e?.ledger_id != null ? Number(e.ledger_id) : null,
          document_status:
            e?.document_status === 'pending' || e?.document_status === 'applied'
              ? e.document_status
              : null,
        }));
        return { success: !!resp?.success, message: String(resp?.message ?? ''), data: normalized };
      },
      transformErrorResponse: handlerErrorResponse('Inventory.Stocktakes.List'),
      providesTags: (_r, _e, arg) => [{ type: 'Stocktake', id: arg.inventory_id }],
    }),
  }),
});

export const {
  useListUomsQuery,
  useListItemsQuery,
  useLazyListItemsQuery,
  useGetItemQuery,
  useSearchItemsQuery,
  useLazySearchItemsQuery,
  useValidateStockMutation,
  useCreateItemMutation,
  useUpdateItemMutation,
  useToggleItemActiveMutation,
  useGetStockQuery,
  useLazyGetStockQuery,
  useGetLedgerQuery,
  useLazyGetLedgerQuery,
  useGetAdjustmentsQuery,
  useLazyGetAdjustmentsQuery,
  useCreateStocktakeMutation,
  useApplyStocktakeMutation,
  useGetStocktakesQuery,
  useLazyGetStocktakesQuery,
  useGetSerialsQuery,
  useLazyGetSerialByIdQuery,
  useGetSerialByIdQuery,
  useCreateSerialMutation,
  useGetBatchesQuery,
  useLazyGetBatchByIdQuery,
  useGetBatchByIdQuery,
  useLabelSerialsMutation,
  useAdjustLedgerMutation,
} = inventoryApi;
